<template>
  <div class="notification-list">
    <!-- 通知列表 -->
    <div class="notification-content" ref="notificationContent" @scroll="handleScroll">
      <el-empty v-if="!loading && notifications.length === 0" description="暂无通知" />
      
      <div v-else class="notification-items">
        <NotificationItem
          v-for="notification in notifications"
          :key="notification.id"
          :ref="`notification-${notification.id}`"
          :notification="notification"
          @mark-as-read="$emit('mark-as-read', $event)"
          @delete="$emit('delete', $event)"
        />
        
        <!-- 滚动到底部时显示的加载更多按钮 -->
        <div v-if="shouldShowLoadMore && showLoadMoreButton" class="notification-load-more-inline">
          <el-button 
            type="primary" 
            :loading="loading"
            @click="handleLoadMore"
            style="width: calc(100% - 40px); margin: 20px;"
          >
            {{ loading ? '加载中...' : '加载更多' }}
          </el-button>
        </div>
        
        <!-- 底部占位，用于触发滚动检测 -->
        <div v-if="shouldShowLoadMore" ref="loadMoreTrigger" class="load-more-trigger"></div>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationItem from './NotificationItem.vue'

export default {
  name: 'NotificationList',
  components: {
    NotificationItem
  },
  props: {
    notifications: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    total: {
      type: Number,
      default: 0
    },
    targetNotificationId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      showLoadMoreButton: false,
      observer: null
    }
  },
  mounted() {
    this.setupIntersectionObserver()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  watch: {
    targetNotificationId(newId) {
      if (newId) {
        this.$nextTick(() => {
          this.scrollToNotification(newId)
        })
      }
    },
    shouldShowLoadMore(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.setupIntersectionObserver()
        })
      }
    },
    notifications: {
      handler(newNotifications) {
        // 当通知列表更新时，重新检查所有通知项的内容高度
        // 这对于新添加的通知项特别重要
        if (newNotifications && newNotifications.length > 0) {
          this.$nextTick(() => {
            // 延迟检查，确保DOM完全渲染
            setTimeout(() => {
              this.checkAllNotificationHeights()
            }, 200)
          })
        }
      },
      immediate: false
    }
  },
  methods: {
    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect()
      }
      
      const options = {
        root: this.$refs.notificationContent,
        rootMargin: '100px',
        threshold: 0.1
      }
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.showLoadMoreButton = true
          } else {
            this.showLoadMoreButton = false
          }
        })
      }, options)
      
      this.$nextTick(() => {
        if (this.$refs.loadMoreTrigger) {
          this.observer.observe(this.$refs.loadMoreTrigger)
        }
      })
    },
    handleScroll() {
      // 可以在这里添加额外的滚动处理逻辑
    },
    handleLoadMore() {
      this.$emit('load-more')
    },
    checkAllNotificationHeights() {
      // 检查所有通知项的内容高度，确保展开按钮正确显示
      if (this.notifications && this.notifications.length > 0) {
        this.notifications.forEach((notification) => {
          const refKey = `notification-${notification.id}`
          const targetRefs = this.$refs[refKey]
          if (targetRefs && targetRefs.length > 0) {
            const targetComponent = targetRefs[0]
            if (targetComponent && typeof targetComponent.forceCheckContentHeight === 'function') {
              targetComponent.forceCheckContentHeight()
            }
          }
        })
      }
    },
    scrollToNotification(notificationId) {
      // console.log('[NotificationList] scrollToNotification被调用，ID:', notificationId)
      if (!notificationId) {
        // console.warn('[NotificationList] notificationId为空')
        return
      }
      
      const refKey = `notification-${notificationId}`
      // console.log('[NotificationList] 查找ref:', refKey)
      // console.log('[NotificationList] 所有refs:', this.$refs)
      const targetRefs = this.$refs[refKey]
      
      if (targetRefs && targetRefs.length > 0) {
        // console.log('[NotificationList] 找到目标组件')
        const targetComponent = targetRefs[0]
        const targetElement = targetComponent.$el
        
        if (targetElement) {
          // console.log('[NotificationList] 找到目标元素，开始滚动')
          
          // 先标记为已读
          this.$emit('mark-as-read', notificationId)
          
          // 滚动到目标元素
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
          
          // 高亮显示
          targetElement.classList.add('highlight-notification')
          setTimeout(() => {
            targetElement.classList.remove('highlight-notification')
          }, 2000)
          
          // 延迟触发展开，确保组件已完全渲染
          setTimeout(() => {
            // 先强制检查高度
            if (targetComponent.forceCheckContentHeight) {
              // console.log('[NotificationList] 调用forceCheckContentHeight')
              targetComponent.forceCheckContentHeight()
            }
            
            // 触发展开内容
            if (targetComponent.expandContent) {
              // console.log('[NotificationList] 调用expandContent')
              targetComponent.expandContent()
            }
          }, 500)
        } else {
          // console.warn('[NotificationList] targetElement不存在')
        }
      } else {
        // console.warn('[NotificationList] 未找到目标ref，可用的通知数量:', this.notifications.length)
        // console.warn('[NotificationList] 通知列表:', this.notifications.map(n => n.id))
      }
    }
  },
  computed: {
    // 只有当总数超过10条且还有更多数据时才显示加载更多按钮
    shouldShowLoadMore() {
      return this.total > 10 && this.hasMore && this.notifications.length > 0
    }
  }
}
</script>

<style scoped>
.notification-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.notification-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  height: 100%;
}

.notification-items {
  padding: 0;
  position: relative;
}

.notification-load-more-inline {
  text-align: center;
  padding: 0;
}

.load-more-trigger {
  height: 20px;
  width: 100%;
  visibility: hidden;
}

/* 滚动条样式 */
.notification-content::-webkit-scrollbar {
  width: 6px;
}

.notification-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.notification-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.notification-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 高亮通知的动画效果 */
.notification-list ::v-deep .highlight-notification {
  animation: highlight-pulse 1s ease-in-out;
  border: 2px solid #366AFF !important;
  box-shadow: 0 0 20px rgba(54, 106, 255, 0.4) !important;
}

@keyframes highlight-pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(54, 106, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 30px rgba(54, 106, 255, 0.6);
  }
}
</style>
